
import { Logger }            from '@/util/logger';
import { LoaderOptions }     from './loader.options';
import {
  StartCallBack,
  FeedbackCallBack,
  ParseSuccessCallBack,
  ParseFailureCallBack,
  EventType,
  ProgressCallBack,
  LoaderCallback
}                            from './loader.events';

import { ChunkReader }       from './chunk.reader';
import { EMCA404 }           from './e.m.c.a.404';
import {
  Listener,
  ParserEvent
}                            from './e.m.c.a.404.events';
import {basename, bytesToSize} from "../util/util";


export enum LoaderState {
  READY,
  LOADING,
  FAILED,
  COMPLETED
}

/**
 *
 * @class JSONLoader
 *
 * @brief mini-framework for loading large JSON files while also supporting
 * progress display, and "online" building of the model from the JSON
 * data.  That is, build the model as the JSON is parsed, rather than waiting
 * until the JSON is fully parsed to even being building the model.
 *
 * We want to ensure the GUI remains responsive, and doesn't "lock up" while
 * we take several seconds to load a large JSON and then even more seconds
 * to build our model.  This means loading the JSON in chunks rather than just
 * reading the whole thing in.  So, we can't use JSON.parser() and we can't
 * use existing javascript libraries because they don't have the features for
 * doing the "online" approach.
 *
 * Some javascript frameworks for JSON parsing do have the features we need;
 * but they were designed for using in Node.js. We can't do this server side,
 * that would take even longer...transfer the JSON to the server, do the work,
 * transfer data back etc.  We have to do it all concisely right here in the
 * browser.
 *
 * NOTE: for performance, Logger.debug(...) lines have been commented out as
 * much as possible.  If you need them, just uncomment, it will just run slightly
 * slower.
 *
 * NOTE: input file must be valid EMCA 404 JSON.
 *
 *   - https://www.json.org/json-en.html
 *   - https://www.ecma-international.org/wp-content/uploads/ECMA-404_2nd_edition_december_2017.pdf
 *
 * Usage:
 *
 *   // upload or drag and drop a File (https://developer.mozilla.org/en-US/docs/Web/API/File),
 *   // so you get a File object to work with, and then...
 *
 *   // create a loader
 *
 *   let loader: JSONLoader = new JSONLoader();
 *
 *   // setup handlers for basic events
 *
 *   loader.onParsingStart((event: ParsingStartEvent) => {
 *
 *     Logger.debug(`parsing started: `, event);
 *
 *   }).onSuccess((event: ParseSuccessEvent) => {
 *
 *     Logger.debug(`parsing completed: `, event);
 *
 *   }).onFailure((event: ParseFailureEvent) => {
 *
 *     Logger.debug(`parsing failed: `, event);
 *   });
 *
 *   // load!
 *
 *   loader.load(file);
 *
 *   //
 *   // loading will happen asynchronously, and your event handlers
 *   // will be called to let you fill in your model progressively.
 *   //
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      May 2023
 *
 */
export class JSONLoader {

  private state: LoaderState = LoaderState.READY;

  /** the JSON file we are reading from */
  private file: File|null = null;

  /** the buffered reader for the file */
  private reader: ChunkReader|null = null;

  /** the buffers we've read so far */
  private bufferCount: number = 0;

  /** the bytes read so far */
  private bytesRead: number = 0;

  /** unique ids for events */
  private static eventId: number = 0;

  /** the size of buffer to use for reading from the source JSON */
  private chunkSize : number;

  /** the listeners for general events around managing the parsing */
  private basicEvents : Map<string, LoaderCallback[]> = new Map<string, LoaderCallback[]>();

  /** milliseconds when we started */
  private loadStartTime: number = 0;

  /** milliseconds when we finished */
  private loadEndTime: number = 0;

  /** the file loaded as a raw string */
  private raw: string = '';

  /** the loading error (if there is one */
  private error: string|null = null;

  /** the progressive parser we use for parsing the JSON */
  private emca404: EMCA404|null = null;

  /**
   * allow the application to register event handlers...even if we don't
   * have a parser yet to actually register the handlers on.  Once we
   * start actually loading...then just register all of the application's
   * handlers before we actually start parsing.
   *
   * @private
   */
  private handlers: Map<string, Listener[]> = new Map<string, Listener[]>();

  /**
   * standard constructor, we have all the state related to loading and
   * progress, so we can't just use static methods here.
   *
   * @param options set any of the supported parameters (if you want to).
   *
   */
  constructor(options?: LoaderOptions) {

    this.chunkSize = options?.chunkSize ?? 256 * 1024;


  }

  /**
   * register an event listener for a specific kind of event
   *
   * @param tag      the kind of event to listen on
   * @param callBack the listener
   *
   * @private
   */
  private addBasicListener(
    tag: string,
    callBack: LoaderCallback): void {

    if (this.basicEvents.has(tag)) {

      // @ts-ignore
      this.basicEvents.get(tag).push(callBack);

    } else {

      /* first listener of this type */

      this.basicEvents.set(tag, [callBack]);
    }

  }

  /**
   * register to listen erfor the start event.
   *
   * @param callBack your function to call for this event.
   */
  onParsingStart(callBack: StartCallBack): JSONLoader {

    this.addBasicListener(EventType.START, callBack);

    return this;
  }

  /**
   * register to listener for errors and warnings
   *
   * @param callBack your function to call for this event.
   */
  public onFeedback(callBack: FeedbackCallBack): JSONLoader {

    this.addBasicListener('feedback', callBack);

    return this;
  }

  /**
   * register to listener for failure
   *
   * @param callBack your function to call for this event.
   */
  public onFailure(callBack: ParseFailureCallBack): JSONLoader {

    this.addBasicListener(EventType.FAILED, callBack);

    return this;
  }

  /**
   * register to listener for success
   *
   * @param callBack your function to call for this event.
   */
  public onSuccess(callBack: ParseSuccessCallBack): JSONLoader {

    this.addBasicListener(EventType.COMPLETED, callBack);

    return this;
  }

  /**
   * register to listener for progress
   *
   * @param callBack your function to call for this event.
   */
  public onProgress(callBack: ProgressCallBack): JSONLoader {

    this.addBasicListener(EventType.PROGRESS, callBack);

    return this;
  }

  /**
   * send basic events out to any listeners
   *
   * @param event the event to publish
   *
   * @private
   */
  private emitBasic(event: any) {

    JSONLoader.eventId++;

    event.id        = JSONLoader.eventId;

    let tag: string = event.tag;

    if (!this.basicEvents.has(tag)) {

      /* no listeners, nothing to do */

      return ;
    }

    // @ts-ignore
    let listeners: (LoaderCallback)[] =
      this.basicEvents.get(tag);

    listeners.forEach((callBack) => {

      callBack(event);

    });
  }

  /** force the chunk size */
  public setChunkSize(chunkSize: number): JSONLoader {

    this.chunkSize = chunkSize;

    return this;
  }

  /** fetch the current chunk size */
  public getChunkSize(): number {

    return this.chunkSize;
  }

  /** reset for a new load */
  private reset() {

    /* reset our state */

    let loader: JSONLoader  = this;

    loader.reader           = null;
    loader.raw              = '';
    loader.error            = '';
    loader.bufferCount      = 0;
    loader.bytesRead        = 0;
    loader.loadStartTime    = 0;
    loader.loadEndTime      = 0;

    loader.state            = LoaderState.READY;

    /* create fresh JSON parser */

    loader.emca404 = new EMCA404().onBufferCompleted((event: ParserEvent) => {

      /* parser is hungry! Trigger another block fetch and parse */

      //Logger.debug(`[JSONLoader] 'EMCA404' buffer completed: `, event);

      /*
       * trigger another chunk read as soon as possible.  This is effectively doing a
       * "yield" to let the GUI do other stuff.  We're trying to avoid locking up the
       * GUI.
       *
       * Note that we are not using Web Workers here, we would like to, but...Angular
       * has some limitations, and one of them is that it can't easily implement a
       * web worker inside a library project (and this is a library project).  THere
       * is a long discussion of the feature here:
       *
       *   https://github.com/angular/angular-cli/issues/15059#issuecomment-747419154
       *
       * But basically...not ready yet, and won't be for a while.  So no point trying
       * to use a web worker inside our library.  For now, we just accept that all the
       * work will be done on the main JavaScript thread, and we will at least allow
       * some yielding by breaking up the processing into chunks.  That is only do
       * the processing chunk by chunk.  Not ideal, but at least the GUI will still be
       * reasonably responsive during the loading of large JSON files.
       *
       */

      setTimeout(() => {

        //Logger.debug(`[JSONLoader] 'EMCA404' triggering next buffer read/parse...`);

        loader.triggerChunk();

      });

    }).onError((event: ParserEvent) => {

      Logger.warning(`[JSONLoader] 'EMCA404' error: `, event);

    }).onWarning((event: ParserEvent) => {

      Logger.warning(`[JSONLoader] 'EMCA404' warning: `, event);

    });

    /* register any known event handlers */

    loader.registerhandlers();

  }

  /** (re)register any known JSON watchers in the current parser */
  private registerhandlers() {

    let emca404: EMCA404|null = this.emca404;

    if (!emca404) {

      return ;
    }

    for (const [path, listener] of this.handlers) {

      listener.forEach((cb: Listener): void => {

        // @ts-ignore
        emca404.on(path, cb);

      });

    }

    /* clear out the ones now registered, so we can't double register later */

    this.handlers.clear();
  }

  /**
   * the trigger for fetching a new buffer of raw data from the source JSON file.
   * we expect the general load loop to be something like this:
   *
   *   - fetch a block of text
   *   - send it to the parser
   *   - respond to parsing events until block is exhausted
   *   - when the parser asks for me input, go back to the top of the loop
   *
   * there is a catch; the parser may not and most likely will not use
   * the bytes near the end of the buffer...buffers won't just conveniently
   * end in between elements of the JSON.  So, the parser will consume as
   * much as it can parse into valid JSON, then ask for more.
   *
   * Either we will stick together the remainder with the next buffer, or
   * the parser will do that internally.
   *
   */
  private triggerChunk() {

    //Logger.debug(`[JSONLoader] 'triggerChunk' starts...`);

    let loader: JSONLoader  = this;

    if (loader.file === null) {

      /* nothing to do */

      Logger.warning(`[JSONLoader] 'triggerChunk' no file is set.`);

      return ;
    }

    /* read a chunk! */

    // @ts-ignore
    this.reader.chunk().then(

      (buffer: string|null) => {

        if (loader.file === null) {

          /* we shouldn't be here! */

          Logger.error(`[JSONLoader] 'triggerChunk' inconsistent, no source file!`);

          return ;
        }

        if (buffer === null) {

          /* time to stop */

          //Logger.debug(`[JSONLoader] 'triggerChunk' file loaded sz=${loader.raw.length}`);

          if (loader.emca404) {

            loader.raw = loader.emca404.rawString();
          }

          /* send out the completion event */

          loader.loadEndTime = Date.now();

          loader.state       = LoaderState.COMPLETED;

          loader.emitBasic({
            tag:     EventType.COMPLETED,
            summary: `Parsing completed. name=${loader.file.name}, sz=${loader.file.size}, parsed=${loader.raw.length}`,
            duration: (loader.loadEndTime - loader.loadStartTime ) / 1000.0
          });

          loader.emitBasic({
            tag:     EventType.PROGRESS,
            summary: `Loaded!`,
            progress: 100
          });

          /* we don't need the parser or reader anymore, let them go for garbage collection */

          loader.emca404 = null;

          loader.reader?.clear();

          // let t1 = Date.now();
          //
          // try {
          //
          //   let ob = JSON.parse(loader.raw);
          //   let t2 = Date.now();
          //
          //   Logger.debug(`[JSONLoader] 'triggerChunk' sz=${loader.raw.length}, parse time: `, (t2 - t1) / 1000.0);
          //   Logger.debug(`[JSONLoader] 'triggerChunk' obj: `, ob);
          //
          // } catch(e) {
          //
          //   Logger.error(`[JSONLoader] 'triggerChunk' JSON.parse exception: `, e);
          //
          // }

        } else {

          /* just keep adding to the overall result */

          loader.raw        += buffer;
          loader.bytesRead  += buffer.length;

          loader.bufferCount++;

          /* emit a progress event */

          let progress: number = (loader.bytesRead / loader.file.size) * 100.0;

          let size1: string    = bytesToSize(loader.bytesRead);
          let size2: string    = bytesToSize(loader.file.size);

          loader.emitBasic({
            tag:     EventType.PROGRESS,
            summary: `Parsed ${size1} of ${size2} : ${basename(loader.file.name)}...`,
            progress
          });

          //Logger.debug(`[JSONLoader] 'triggerChunk' read chunk sz=${buffer.length}, byte=${loader.bytesRead}, buffers=${loader.bufferCount}`);

          /* do the parsing for this buffer ... */

          if (loader.emca404 !== null) {

            loader.emca404.parseBuffer(buffer);
          }

        }

      },
      (error: string) => {

        if (loader.file === null) {

          /* we shouldn't be here! */

          //Logger.debug(`[JSONLoader] 'triggerChunk' (error) inconsistent, no source file!`);

          return ;
        }

        //Logger.debug(`[JSONLoader] 'triggerChunk' (error) problem reading file ${error}`);

        /* send out the failed event */

        loader.state       = LoaderState.FAILED;

        loader.emitBasic({
          tag:     EventType.FAILED,
          summary: `${error} name=${loader.file.name}, sz=${loader.file.size}`,
        });

        loader.emitBasic({
          tag:     EventType.PROGRESS,
          summary: `Failed! ${basename(loader.file.name)}...`,
          progress: 100
        });

      }).catch((error: string) => {

        if (loader.file === null) {

          /* we shouldn't be here! */

          Logger.error(`[JSONLoader] 'triggerChunk' (catch) inconsistent, no source file!`);

          return ;
        }

        Logger.error(`[JSONLoader] 'triggerChunk' (catch) problem reading file ${error}`);

        /* send out the failed event */

        loader.state       = LoaderState.FAILED;

        loader.emitBasic({
          tag:     EventType.FAILED,
          summary: `${error} name=${loader.file.name}, sz=${loader.file.size}`,
        });

      });

  }

  /*
   *
   * launch JSON parsing and model building. To gather the parsed
   * data add event watchers via the watch() method.  As the data
   * is parsed, you can then respond by building up your model
   * appropriately.
   *
   * To monitor progress pass in a progress reporter.
   *
   * @param file the JSON file we need to parser and build a model from
   */
  public load(file: File): boolean {

    //Logger.debug(`[JSONLoader] 'load' starts, file: `, file);

    let loader: JSONLoader  = this;

    if (loader.file) {

      if (loader.state != LoaderState.READY) {

        /* loading has already started, and may actually be completed or failed */

        let signature1: string = loader.file.name + loader.file.size.toString() + loader.file.lastModified.toString();
        let signature2: string = file.name + file.size.toString() + file.lastModified.toString();

        if (signature1 != signature2) {

          /* trying to load a different file, while one is already being loaded. */

          Logger.error(`[JSONLoader] trying to load different file, while file is already being loaded.`);

          return false;
        }

        /* its the same file that is already being loaded, or is already completed one way or the other */

        //Logger.debug(`[JSONLoader] (re)requested load of same file.`);

        return true;
      }
    }

    /* reset the current file we are working on */

    loader.reset();

    loader.file   = file;
    loader.reader = new ChunkReader(loader.file, loader.chunkSize);

    loader.state       = LoaderState.LOADING;

    loader.emitBasic({
      tag:     EventType.START,
      summary: `Parsing starts. name=${file.name}, sz=${file.size}`
    });

    loader.emitBasic({
      tag:     EventType.PROGRESS,
      summary: `Loading ${basename(loader.file.name)}...`,
      progress: 0
    });

    /*
     * use a reader to read the JSON file in chunks, this is basically what gives
     * us a read loop with some "yield", when we read a chunk, we give thread
     * control back to the browser, so everytime we read a chunk there is also
     * context switching happening and this gives the browser a chance to do other
     * things.
     *
     * This is how we avoid locking up the GUI.
     *
     * In addition, we do the actual parsing in a web worker, which has its own thread,
     * so again, we aren't locking up the GUI.  The only time we are taking control
     * is for responding to the objects being parsed events.  That is, the actual model
     * building.  But...we're only doing as much of that as we get events, and we can
     * only get as many parse events as are generated from a chunk of text being
     * parsed, so...just make sure you don't make the chunk size so big, that the GUI
     * starts to the stutter.
     *
     */

    loader.loadStartTime = Date.now();

    setTimeout( () => {

      /*
       * yield so other stuff can happen, and let the current code finish before
       * we start parsing...there may be other initialization going on.
       *
       */

      loader.triggerChunk();
    })

    return true;
  }

  /** fetch the string that was loaded, only meaningful after the parse complete event. */
  public toString(): string {

    if (this.raw === null) {

      return '';
    }

    return this.raw;
  }

  /**
   * parse the raw data using JSON.parser() and return the result, only
   * meaningful after the parse 'complete' event.
   *
   */
  public toObject(): any {

    let obj: any = null;

    try {

      obj = JSON.parse(this.raw);

    } catch (e) {

      Logger.error(`[JSONLoader] 'toObject' exception: `, e);

    }

    return obj;
  }

  /**
   * register a callback for the given JSON object path.  You can use
   * basic Javascript like syntax.  For example:
   *
   *   user/first.name
   *
   * to listen for when the first.name field is pared in the object user.  You
   * can use [] for array elements, and you can use a glob character (*) for
   * field names or for array indexes.  more examples:
   *
   *   a/a[3][0]
   *   b[0]/c
   *   aa/*
   *
   * Keep in mind the glob character (*) is not recursive, it is only applied to
   * the level where you use it.
   *
   * @param path
   * @param listener
   */
  public on(path: string, listener: Listener): void {

    if (this.handlers.has(path)) {

      // @ts-ignore
      this.handlers.get(path).push(listener);

    }

    /* first listener of this type */

    this.handlers.set(path, [listener]);

  }

}
